"use client";

import {
  DEFAULT_AI_MODEL_ID,
  resolveAllowlistedModelId,
} from "@/lib/ai-model-options";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

interface IAIModelContext {
  selectedModelId: string;
  setSelectedModelId: (modelId: string) => void;
  /** Stable ref-backed getter — always returns the latest model ID even inside stale closures. */
  getModelId: () => string;
}

const aiModelContext = createContext<IAIModelContext>({
  selectedModelId: DEFAULT_AI_MODEL_ID,
  setSelectedModelId: () => undefined,
  getModelId: () => DEFAULT_AI_MODEL_ID,
});

/**
 * Provides the currently selected AI model for all chat sidebars.
 * State is session-scoped (resets on page refresh) and shared across all
 * sidebars rendered under this provider.
 *
 * @param children - React subtree.
 * @returns Provider element wrapping the subtree.
 */
export function AIModelProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [selectedModelId, setModelIdState] =
    useState<string>(DEFAULT_AI_MODEL_ID);
  const modelIdRef = useRef<string>(DEFAULT_AI_MODEL_ID);

  const setSelectedModelId = useCallback((id: string) => {
    const resolved = resolveAllowlistedModelId(id);

    modelIdRef.current = resolved;
    setModelIdState(resolved);
  }, []);

  const getModelId = useCallback(() => modelIdRef.current, []);

  const value = useMemo(
    () => ({ selectedModelId, setSelectedModelId, getModelId }),
    [selectedModelId, setSelectedModelId, getModelId],
  );

  return (
    <aiModelContext.Provider value={value}>
      {children}
    </aiModelContext.Provider>
  );
}

/**
 * Consumes the AI model context.
 * Must be rendered inside {@link AIModelProvider}.
 *
 * @returns Current AI model context value.
 */
export function useAIModel(): IAIModelContext {
  return useContext(aiModelContext);
}
